import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router";
import { motion, AnimatePresence } from "framer-motion";
import { Loader2, Mail, Lock, Eye, EyeOff, Inbox, X, AlertCircle } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import AuthSplitLayout from "../../components/auth/AuthSplitLayout";
import { loginStart, loginSuccess, loginFailure, clearLoginError } from "../../store/slices/userSlice";
import { requestConsumerLoginOtp, getErrorMessage } from "../../api/userAuthApi";
import showToast  from "../../utils/toast";

const inputClassName =
  "w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-[#FFD221] focus:ring-4 focus:ring-[#FFD221]/20";

/** Mirrors userSlice loginSuccess token extraction — true when the user has a session. */
function payloadHasSessionToken(payload) {
  const data = payload?.data;
  const dataObj = data && typeof data === "object" && !Array.isArray(data) ? data : null;
  return Boolean(
    payload?.token ??
      payload?.access_token ??
      dataObj?.token ??
      dataObj?.access_token ??
      null,
  );
}

const SignIn = (props = {}) => {
  const {
    embed = false,
    onAuthenticated,
    onRequestSignUp,
    onRequestForgot,
    initialEmail: embedInitialEmail,
    fromRegistration: embedFromRegistration,
  } = props;

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { loginStatus, loginError } = useSelector((s) => s.infoboxx_consumer);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [inboxReminderOpen, setInboxReminderOpen] = useState(false);
  const [reminderEmail, setReminderEmail] = useState("");
  const passwordInputRef = useRef(null);

  useEffect(() => {
    if (embed) return;
    window.scrollTo(0, 0);
  }, [embed]);

  /** Modal / embed: parent passes registration hand-off. */
  useEffect(() => {
    if (!embed) return;
    if (embedInitialEmail) {
      setEmail(String(embedInitialEmail).trim());
    }
    if (embedFromRegistration) {
      setInboxReminderOpen(true);
      setReminderEmail(String(embedInitialEmail || "").trim());
    }
  }, [embed, embedInitialEmail, embedFromRegistration]);

  /** Full page: consume signup hand-off from router state. */
  useEffect(() => {
    if (embed) return;
    const st = location.state;
    const prefilled = st?.prefilledEmail;
    const fromReg = Boolean(st?.fromRegistration);
    const redirect = st?.postAuthRedirect;

    if (prefilled) {
      setEmail(String(prefilled).trim());
    }
    if (fromReg) {
      setInboxReminderOpen(true);
      setReminderEmail(String(prefilled || "").trim());
    }

    if (prefilled || fromReg) {
      navigate(location.pathname, {
        replace: true,
        state: redirect ? { postAuthRedirect: redirect } : {},
      });
    }
  }, [embed, location.pathname, location.state, navigate]);

  useEffect(() => {
    if (!inboxReminderOpen) return;
    const id = window.setTimeout(() => {
      passwordInputRef.current?.focus({ preventScroll: true });
    }, 320);
    return () => window.clearTimeout(id);
  }, [inboxReminderOpen]);

  const submitting = loginStatus === "loading";
  const fieldIssueClass = loginError
    ? "border-red-400 focus:border-red-500 focus:ring-red-500/25"
    : "";

  const credentialHint = (() => {
    if (!loginError) return false;
    const m = String(loginError).toLowerCase();
    return (
      m.includes("credential") ||
      m.includes("password") ||
      m.includes("invalid user") ||
      m.includes("incorrect")
    );
  })();

  const handleSubmit = async (e) => {
    e.preventDefault();
    const cleanEmail = email.trim();
    if (!cleanEmail) {
      showToast.warning("Enter your email to continue.");
      return;
    }
    if (!password) {
      showToast.warning("Enter your password to continue.");
      return;
    }
    try {
      dispatch(loginStart());
      const payload = await requestConsumerLoginOtp({ email: cleanEmail, password });
      const merged = { email: cleanEmail, ...payload };
      dispatch(loginSuccess(merged));
      if (embed) {
        if (payloadHasSessionToken(merged)) {
          onAuthenticated?.();
        }
        return;
      }
      const raw = location.state?.postAuthRedirect;
      const to =
        typeof raw === "string" && raw.trim()
          ? raw.trim()
          : raw && typeof raw === "object" && raw.path
            ? raw.path
            : "/";
      const nextState =
        raw && typeof raw === "object" && raw.path && raw.state !== undefined
          ? raw.state
          : undefined;
      navigate(to, {
        replace: true,
        ...(nextState !== undefined ? { state: nextState } : {}),
      });
    } catch (err) {
      const message = getErrorMessage(err);
      dispatch(loginFailure(message));
    }
  };

  const formContent = (
    <>
      <AnimatePresence>
        {inboxReminderOpen ? (
          <motion.div
            layout
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ type: "spring", damping: 24, stiffness: 320 }}
            role="status"
            aria-live="polite"
            className="relative overflow-hidden rounded-2xl border border-[#FFD221]/35 bg-gradient-to-br from-[#FFD221]/12 via-white to-amber-50/40 p-4 shadow-sm ring-1 ring-black/[0.03]"
          >
            <div className="pointer-events-none absolute -right-8 -top-10 h-24 w-24 rounded-full bg-[#FFD221]/25 blur-2xl" />
            <div className="relative flex gap-3.5">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#FFD221]/90 text-black shadow-sm">
                <Inbox className="w-5 h-5" strokeWidth={2.5} aria-hidden />
              </div>
              <div className="min-w-0 flex-1 pt-0.5">
                <p className="text-sm font-black tracking-tight text-slate-900">
                  Your password is in your inbox
                </p>
                <p className="mt-1.5 text-sm leading-relaxed text-slate-600">
                  We just emailed login details
                  {reminderEmail ? (
                    <>
                      {" "}
                      to{" "}
                      <span className="font-semibold break-all text-slate-800">{reminderEmail}</span>
                    </>
                  ) : null}
                  . Check your inbox and spam or promotions — then paste your password below.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setInboxReminderOpen(false)}
                className="absolute right-2 top-2 rounded-lg p-1.5 text-slate-400 transition hover:bg-white/80 hover:text-slate-700"
                aria-label="Dismiss notice"
              >
                <X size={16} strokeWidth={2.5} />
              </button>
            </div>
          </motion.div>
        ) : null}
      </AnimatePresence>

      <form className="space-y-3" onSubmit={handleSubmit}>
        <label className="block space-y-1.5 pt-0.5">
          <span className="text-xs font-semibold tracking-wide uppercase text-slate-500">Email address</span>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none text-slate-400" size={17} />
            <input
              className={`pl-10 ${inputClassName} ${fieldIssueClass}`}
              type="email"
              autoComplete="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => {
                dispatch(clearLoginError());
                setEmail(e.target.value);
              }}
              disabled={submitting}
              required
            />
          </div>
        </label>

        <label className="block space-y-1.5">
          <span className="text-xs font-semibold tracking-wide uppercase text-slate-500">Password</span>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none text-slate-400" size={17} />
            <input
              ref={passwordInputRef}
              className={`pr-10 pl-10 ${inputClassName} ${fieldIssueClass}`}
              type={showPassword ? "text" : "password"}
              autoComplete="current-password"
              placeholder="Enter your password"
              value={password}
              onChange={(e) => {
                dispatch(clearLoginError());
                setPassword(e.target.value);
              }}
              disabled={submitting}
              required
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 transition-colors -translate-y-1/2 text-slate-400 hover:text-slate-600"
            >
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
        </label>

        <AnimatePresence mode="sync">
          {loginError ? (
            <motion.div
              key="signin-error"
              role="alert"
              aria-live="assertive"
              initial={{ opacity: 0, y: -6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.2 }}
              className="relative overflow-hidden rounded-2xl border border-red-200/90 bg-red-50/95 p-4 text-left shadow-sm ring-1 ring-red-100"
            >
              <div className="flex gap-3 pr-7">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-red-100 text-red-700">
                  <AlertCircle className="h-5 w-5" strokeWidth={2.25} aria-hidden />
                </div>
                <div className="min-w-0 flex-1 space-y-1 pt-0.5">
                  <p className="text-sm font-bold text-red-950">
                    {credentialHint ? "We couldn't sign you in" : "Something went wrong"}
                  </p>
                  <p className="text-sm leading-relaxed text-red-900/90 break-words">{loginError}</p>
                  {credentialHint ? (
                    <p className="pt-1 text-xs leading-relaxed text-red-800/85">
                      Double-check your email and password, or{" "}
                      {embed ? (
                        <button
                          type="button"
                          onClick={() => onRequestForgot?.(email.trim())}
                          className="font-semibold text-red-950 underline decoration-red-300 underline-offset-2 hover:decoration-red-500"
                        >
                          reset your password
                        </button>
                      ) : (
                        <Link
                          to="/forgot-password"
                          className="font-semibold text-red-950 underline decoration-red-300 underline-offset-2 hover:decoration-red-500"
                        >
                          reset your password
                        </Link>
                      )}
                      .
                    </p>
                  ) : null}
                </div>
              </div>
              <button
                type="button"
                onClick={() => dispatch(clearLoginError())}
                className="absolute right-2 top-2 rounded-lg p-1.5 text-red-400 transition hover:bg-white/90 hover:text-red-700"
                aria-label="Dismiss error"
              >
                <X size={16} strokeWidth={2.5} />
              </button>
            </motion.div>
          ) : null}
        </AnimatePresence>

        <div className="flex justify-end">
          {embed ? (
            <button
              type="button"
              onClick={() => onRequestForgot?.(email.trim())}
              className="text-xs transition text-slate-500 hover:text-black"
            >
              Forgot password?
            </button>
          ) : (
            <Link to="/forgot-password" className="text-xs transition text-slate-500 hover:text-black">
              Forgot password?
            </Link>
          )}
        </div>

        <motion.button
          whileHover={{ scale: submitting ? 1 : 1.01 }}
          whileTap={{ scale: submitting ? 1 : 0.99 }}
          type="submit"
          disabled={submitting}
          className="flex gap-2 justify-center items-center px-4 py-3 w-full text-sm font-black text-black rounded-xl shadow-lg transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
          style={{ background: "#FFD221" }}
        >
          {submitting ? (
            <>
              <Loader2 size={18} className="animate-spin" />
              Signing in...
            </>
          ) : (
            "Sign in"
          )}
        </motion.button>
      </form>

      {embed ? (
        <p className="text-center text-sm text-slate-600">
          New to Infoboxx?{" "}
          <button
            type="button"
            onClick={() => onRequestSignUp?.()}
            className="font-semibold text-black underline-offset-2 hover:text-[#E5B800]"
          >
            Register free
          </button>
        </p>
      ) : null}
    </>
  );

  if (embed) {
    return <div className="space-y-4 sm:space-y-5">{formContent}</div>;
  }

  return (
    <AuthSplitLayout
      title="Welcome back"
      subtitle="Enter your email and password to access your account."
      sideBadge="INFOBOXX LOGIN"
      sideTitle="Get answers to Ghana's most-asked questions."
      sideDescription="Sign in to access personalized content, expert connections, and your saved topics across Infoboxx."
      sideBullets={[
        "Secure password-protected access",
        "Fast and secure authentication",
        "Access your saved topics and expert connections",
      ]}
      footer={
        <p>
          New to Infoboxx?{" "}
          <Link to="/signup" className="font-semibold text-black hover:text-[#E5B800]">
            Register free
          </Link>
        </p>
      }
    >
      <div className="space-y-3">{formContent}</div>
    </AuthSplitLayout>
  );
};

export default SignIn;
